import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { vehicleOwnerService } from '../../services/vehicleOwnerService';
import { useAuth } from '../../context/AuthContext';
import Alert from '../common/Alert';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
} from '../common/index.js';

const VehicleOwnerList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({ search: '' });
  const perPage = 10;

  const canManage = user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant';
  const canDelete = user?.role === 'System Admin' || user?.role === 'Admin';

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchOwners(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchOwners(currentPage);
  }, [currentPage]);

  const fetchOwners = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search.trim()) filters.owner_name = filterValues.search.trim();
      const response = await vehicleOwnerService.getAllOwners(page, perPage, filters);
      const data = response.data;
      setOwners(data.data || []);
      if (data.pagination) {
        setTotalPages(data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching vehicle owners:', error);
      Alert.error('Failed to load vehicle owners');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ownerId, ownerName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${ownerName}"? This action cannot be undone.`,
      'Delete Vehicle Owner'
    );
    if (!confirmed) return;
    try {
      await vehicleOwnerService.deleteOwner(ownerId);
      Alert.success('Vehicle owner deleted successfully');
      if (owners.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchOwners();
      }
    } catch (error) {
      console.error('Error deleting owner:', error);
      Alert.error(error.response?.data?.error || 'Failed to delete vehicle owner');
    }
  };

  const columns = [
    {
      key: 'owner_name',
      label: 'Owner Name',
      render: (owner) => (
        <button
          onClick={() => navigate(`/vehicle-owners/view/${owner.vehicle_owner_id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {owner.owner_name}
        </button>
      ),
    },
    { key: 'owner_email', label: 'Email' },
    { key: 'owner_contact', label: 'Contact' },
    {
      key: 'vehicles',
      label: 'Vehicles',
      render: (owner) =>
        owner.vehicles && owner.vehicles.length > 0 ? (
          <span className="px-2 py-0.5 bg-brand-light text-brand text-xs font-semibold rounded-full">
            {owner.vehicles.length} vehicle{owner.vehicles.length > 1 ? 's' : ''}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">None</span>
        ),
    },
    {
      key: 'vehicle_owner_percentage',
      label: 'Commission %',
      render: (owner) =>
        owner.vehicle_owner_percentage != null ? `${owner.vehicle_owner_percentage}%` : '0%',
    },
    {
      key: 'bank_account',
      label: 'Bank Account',
      render: (owner) =>
        owner.bank_account || <span className="text-gray-400">—</span>,
    },
  ];

  const getActions = (owner) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/vehicle-owners/view/${owner.vehicle_owner_id}`),
    },
    {
      label: 'Edit Owner',
      icon: Pencil,
      onClick: () => navigate(`/vehicle-owners/edit/${owner.vehicle_owner_id}`),
      visible: canManage,
    },
    {
      label: 'Delete Owner',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(owner.vehicle_owner_id, owner.owner_name),
      visible: canDelete,
    },
  ];

  return (
    <PageLayout
      title="Vehicle Owners"
      icon={Users}
      actions={
        canManage ? (
          <Button icon={Plus} onClick={() => navigate('/vehicle-owners/add')}>
            Add Vehicle Owner
          </Button>
        ) : undefined
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Search by owner name...' },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ search: '' })}
      />

      <DataTable
        columns={columns}
        data={owners}
        loading={loading}
        emptyMessage="No vehicle owners found"
        rowKey="vehicle_owner_id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default VehicleOwnerList;
